import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  InputAdornment,
  ListSubheader,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Checkbox,
  ListItemText,
  Container,
  CircularProgress,
  useMediaQuery,
  Card,
  CardContent,
  Paper,
  ButtonBase
} from '@mui/material';
import { getCategoryIconsForType, CategoryIconBadge } from '../utils/categoryIcons';
import { evaluateAmount, isAmountExpression, formatAmountExpression } from '../utils/amountExpression';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Sort as SortIcon,
  Category as CategoryIcon,
  ArrowUpward as IncomeIcon,
  ArrowDownward as ExpenseIcon,
  ArrowDownward,
  AccountBalance as BankIcon,
  CreditCard as CreditCardIcon,

  CompareArrows as CompareArrowsIcon,
  Clear as ClearIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  ViewSidebar as ViewSidebarIcon,
  Filter as FilterIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  FilterAltOff as FilterAltOffIcon,
  Refresh as RefreshIcon,
  EventOutlined as EventOutlinedIcon
} from '@mui/icons-material';
import { useSupabase } from '../contexts/SupabaseContext';
import { formatCurrency, formatDate } from '../utils/format';
import { fetchAllRows } from '../utils/fetchAllRows';


import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { PieChart as MuiPieChart } from '@mui/x-charts/PieChart';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { useNavigate } from 'react-router-dom';
import GuidedTour from '../components/tour/GuidedTour';
import TourButton from '../components/tour/TourButton';
import useTour from '../components/tour/useTour';
import { TOURS } from '../components/tour/tours';

// Extend dayjs with plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// Transaction types
const transactionTypes = [
  { value: 'income', label: 'Income', icon: <IncomeIcon color="success" /> },
  { value: 'expense', label: 'Expense', icon: <ExpenseIcon color="error" /> },
  { value: 'transfer', label: 'Transfer', icon: <CompareArrowsIcon color="info" /> }
];

// Sorting options at the top with other constants
const sortOptions = [
  { value: 'latest', label: 'Latest First', field: 'created_at', order: 'desc' },
  { value: 'oldest', label: 'Oldest First', field: 'created_at', order: 'asc' },
  { value: 'amount_desc', label: 'Highest Amount', field: 'amount', order: 'desc' },
  { value: 'amount_asc', label: 'Lowest Amount', field: 'amount', order: 'asc' }
];


// Date periods for the Transactions filter. The page loads only the chosen period
// from the database (This month by default) so it opens quickly.
const PERIODS = [
  { value: 'last7', label: 'Last 7 days' },
  { value: 'month', label: 'This month' },
  { value: 'quarter', label: 'This quarter' },
  { value: 'fy', label: 'This FY' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom' },
];

// [from, to] as start-of-day dayjs values (inclusive); null means open-ended
const periodRange = (period, custom = [null, null]) => {
  const today = dayjs().startOf('day');
  switch (period) {
    case 'last7':
      return [today.subtract(6, 'day'), today];
    case 'month':
      return [today.startOf('month'), today.endOf('month').startOf('day')];
    case 'quarter': {
      const start = dayjs(new Date(today.year(), Math.floor(today.month() / 3) * 3, 1));
      return [start, start.add(2, 'month').endOf('month').startOf('day')];
    }
    case 'fy': {
      // Indian financial year: 1 April to 31 March
      const startYear = today.month() >= 3 ? today.year() : today.year() - 1;
      return [dayjs(new Date(startYear, 3, 1)), dayjs(new Date(startYear + 1, 2, 31))];
    }
    case 'custom':
      return [custom[0] ? dayjs(custom[0]).startOf('day') : null, custom[1] ? dayjs(custom[1]).startOf('day') : null];
    default:
      return [null, null];
  }
};

const sameDay = (a, b) => (!a && !b) || (a && b && dayjs(a).isSame(dayjs(b), 'day'));
const sameRange = (a, b) => sameDay(a[0], b[0]) && sameDay(a[1], b[1]);

const describeRange = ([from, to]) => {
  const fmt = (d) => d.format('D MMM YYYY');
  if (!from && !to) return 'All time';
  if (from && !to) return `From ${fmt(from)}`;
  if (!from && to) return `Up to ${fmt(to)}`;
  return `${fmt(from)} – ${fmt(to)}`;
};

const DEFAULT_FILTERS = {
  period: 'month',
  custom: [null, null],
  search: '',
  types: [],
  wallets: [],
  categories: [],
};

// Update CategoryDialog to handle editing
const CategoryDialog = ({ open, onClose, onSave, category = null }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'expense', // Default to expense
    color: '#1976d2',
    icon: null
  });

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name,
        type: category.type,
        color: category.color,
        icon: category.icon || null
      });
    } else {
      // Reset form when creating new category
      setFormData({
        name: '',
        type: 'expense',
        color: '#1976d2',
        icon: null
      });
    }
  }, [category, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      await onSave(formData, category?.id);
    } catch (error) {
      console.error('Error saving category:', error);
    } finally {
      setIsSubmitting(false); // Reset loading state regardless of success or failure
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      // Switching Expense/Income drops an icon that only fits the other type
      if (name === 'type' && prev.icon && !getCategoryIconsForType(value).some(icon => icon.key === prev.icon)) {
        next.icon = null;
      }
      return next;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="category-dialog-title"
      disableEnforceFocus
      onBackdropClick={() => {
        // Clear focus before closing
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        onClose();
      }}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          background: theme => alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(10px)',
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle id="category-dialog-title">
          {category ? 'Edit Category' : 'New Category'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Category Name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                required
                autoFocus
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: theme => alpha(theme.palette.grey[100], 0.8),
                    borderRadius: 3,
                    border: 'none',
                    '& fieldset': {
                      border: `1px solid ${alpha('#000', 0.1)}`,
                    },
                    '&:hover fieldset': {
                      border: `1px solid ${alpha('#000', 0.2)}`,
                    },
                    '&.Mui-focused fieldset': {
                      border: `2px solid`,
                      borderColor: 'primary.main',
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  label="Type"
                  sx={{
                    bgcolor: theme => alpha(theme.palette.grey[100], 0.8),
                    borderRadius: 3,
                    border: 'none',
                    '& fieldset': {
                      border: `1px solid ${alpha('#000', 0.1)}`,
                    },
                    '&:hover fieldset': {
                      border: `1px solid ${alpha('#000', 0.2)}`,
                    },
                    '&.Mui-focused fieldset': {
                      border: `2px solid`,
                      borderColor: 'primary.main',
                    }
                  }}
                >
                  <MenuItem value="expense">Expense</MenuItem>
                  <MenuItem value="income">Income</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Color</InputLabel>
                <OutlinedInput
                  name="color"
                  type="color"
                  value={formData.color}
                  onChange={handleChange}
                  label="Color"
                  sx={{
                    bgcolor: theme => alpha(theme.palette.grey[100], 0.8),
                    borderRadius: 3,
                    border: 'none',
                    '& fieldset': {
                      border: `1px solid ${alpha('#000', 0.1)}`,
                    },
                    '&:hover fieldset': {
                      border: `1px solid ${alpha('#000', 0.2)}`,
                    },
                    '&.Mui-focused fieldset': {
                      border: `2px solid`,
                      borderColor: 'primary.main',
                    }
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Icon <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
              </Typography>
              <Box
                role="radiogroup"
                aria-label="Category icon"
                sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(44px, 1fr))', gap: 1 }}
              >
                {getCategoryIconsForType(formData.type).map(({ key, label, Icon }) => {
                  const selected = formData.icon === key;
                  return (
                    <ButtonBase
                      key={key}
                      disableRipple
                      role="radio"
                      aria-checked={selected}
                      aria-label={label}
                      onClick={() => setFormData((prev) => ({ ...prev, icon: selected ? null : key }))}
                      sx={{
                        height: 44,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: selected ? formData.color : 'divider',
                        bgcolor: selected ? formData.color : 'transparent',
                        color: selected ? '#fff' : 'text.secondary',
                        transition: 'all .15s',
                        '&:hover': { borderColor: formData.color, color: selected ? '#fff' : formData.color },
                        '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                      }}
                    >
                      <Icon fontSize="small" />
                    </ButtonBase>
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mt: 2, p: 1.25, borderRadius: 2, bgcolor: theme => alpha(theme.palette.grey[100], 0.8) }}>
                <Typography variant="caption" color="text.secondary">Preview</Typography>
                <CategoryIconBadge category={formData} size={28} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formData.name || 'Category name'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={(e) => {
              e.preventDefault();
              // Clear focus before closing
              if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
              }
              onClose();
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || !formData.name}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? 'Saving...' : (category ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// Add this new component for category management dialog
const CategoryManagementDialog = ({ open, onClose, categories, onEdit, onDelete }) => {
  const [selectedType, setSelectedType] = useState('expense');

  const handleTypeChange = (event, newValue) => {
    if (newValue !== null) {
      setSelectedType(newValue);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
      onBackdropClick={() => {
        // Clear focus before closing
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        onClose();
      }}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          background: theme => alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(10px)',
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Manage Categories</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              // Clear focus before opening new dialog
              if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
              }
              onEdit();
            }}
            size="small"
          >
            Add Category
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Tabs
          value={selectedType}
          onChange={handleTypeChange}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            value="expense"
            label="Expense Categories"
            icon={<ExpenseIcon color="error" />}
            iconPosition="start"
          />
          <Tab
            value="income"
            label="Income Categories"
            icon={<IncomeIcon color="success" />}
            iconPosition="start"
          />
        </Tabs>

        <Grid container spacing={2}>
          {categories
            .filter(cat => cat.type === selectedType)
            .map((category) => (
              <Grid item xs={12} sm={6} md={4} key={category.id}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <CategoryIconBadge category={category} size={32} />

                  <Typography sx={{ flex: 1 }}>
                    {category.name}
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={() => onEdit(category)}
                    sx={{ color: 'primary.main' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onDelete(category.id)}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};



// Update the combineTransfers function to handle transfer pairs correctly
const combineTransfers = (transactions) => {
  const result = [];
  const processedIds = new Set();

  transactions.forEach(transaction => {
    // Skip if already processed or not a transfer
    if (processedIds.has(transaction.id) || transaction.type !== 'transfer') {
      if (transaction.type !== 'transfer') {
        result.push(transaction);
      }
      return;
    }

    // Find the matching transfer transaction
    const pairedTransaction = transactions.find(t =>
      t.id !== transaction.id &&
      t.type === 'transfer' &&
      t.date === transaction.date &&
      Math.abs(t.amount) === Math.abs(transaction.amount) &&
      Math.sign(t.amount) === -Math.sign(transaction.amount)
    );

    if (pairedTransaction) {
      // Determine which is the 'from' transaction (negative amount)
      const [fromTx, toTx] = transaction.amount < 0
        ? [transaction, pairedTransaction]
        : [pairedTransaction, transaction];

      // Mark both transactions as processed
      processedIds.add(fromTx.id);
      processedIds.add(toTx.id);

      // Create combined transfer transaction
      result.push({
        ...fromTx,
        toWallet: toTx.wallet,
        pairedTransactionId: toTx.id,
        displayAmount: Math.abs(fromTx.amount),
        description: `Transfer: ${fromTx.wallet.name} → ${toTx.wallet.name}`
      });
    } else {
      // If no pair found, treat as a regular transaction
      processedIds.add(transaction.id);
      result.push(transaction);
    }
  });

  return result;
};

// Add this helper function at the top level
const numberToWords = (num) => {
  const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertLessThanThousand = (n) => {
    if (n === 0) return '';

    if (n < 20) return units[n];

    if (n < 100) {
      return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + units[n % 10] : '');
    }

    return units[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convertLessThanThousand(n % 100) : '');
  };

  if (num === 0) return 'Zero';

  const parts = [];
  if (num >= 100000) {
    parts.push(convertLessThanThousand(Math.floor(num / 100000)) + ' Lakh');
    num %= 100000;
  }

  if (num >= 1000) {
    parts.push(convertLessThanThousand(Math.floor(num / 1000)) + ' Thousand');
    num %= 1000;
  }

  if (num > 0) {
    parts.push(convertLessThanThousand(num));
  }

  return parts.join(' ');
};

// Wallet picker: hide archived wallets (unless already selected), group Cash → Credit Card → Bank, A to Z
const WALLET_GROUPS = [
  { type: 'cash', label: 'Cash' },
  { type: 'credit', label: 'Credit Cards' },
  { type: 'bank', label: 'Bank' },
];

const walletMenuItems = (wallets, { excludeId, keepId } = {}) => {
  const usable = wallets.filter(wallet =>
    wallet.id !== excludeId && (!wallet.is_archived || wallet.id === keepId)
  );
  const byName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
  const known = WALLET_GROUPS.map(group => group.type);
  const groups = [
    ...WALLET_GROUPS,
    { type: null, label: 'Other' },
  ].map(group => ({
    ...group,
    items: usable
      .filter(wallet => (group.type ? wallet.type === group.type : !known.includes(wallet.type)))
      .sort(byName),
  }));

  return groups
    .filter(group => group.items.length > 0)
    .flatMap(group => [
      <ListSubheader key={`h-${group.label}`} sx={{ lineHeight: '32px', fontWeight: 700 }}>
        {group.label}
      </ListSubheader>,
      ...group.items.map(wallet => (
        <MenuItem key={wallet.id} value={wallet.id}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceWalletIcon
              sx={{ color: wallet.type === 'credit' ? 'error.main' : 'primary.main' }}
            />
            <Box>
              {wallet.name}
              <Typography variant="caption" color="text.secondary" display="block">
                Balance: {formatCurrency(wallet.balance)}
              </Typography>
            </Box>
          </Box>
        </MenuItem>
      )),
    ]);
};

// Phone view: compact cards grouped under Today / Yesterday / DD-MM-YYYY
const dayLabel = (date) => {
  const d = dayjs(date);
  const today = dayjs().startOf('day');
  if (d.isSame(today, 'day')) return 'Today';
  if (d.isSame(today.subtract(1, 'day'), 'day')) return 'Yesterday';
  return d.format('DD-MM-YYYY');
};

const MobileTransactionList = ({ transactions, categories, onEdit, onDelete }) => {
  // Group consecutive rows by date so the current sort order is kept
  const groups = [];
  transactions.forEach((transaction) => {
    const last = groups[groups.length - 1];
    if (last && last.date === transaction.date) last.items.push(transaction);
    else groups.push({ date: transaction.date, items: [transaction] });
  });

  return (
    <Stack spacing={1.5}>
      {groups.map((group, index) => {
        const net = group.items.reduce((sum, t) => sum + (t.type === 'transfer' ? 0 : Number(t.amount) || 0), 0);
        return (
          <Box key={`${group.date}-${index}`}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', px: 0.5, mb: 0.75 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {dayLabel(group.date)}
              </Typography>
              {net !== 0 && (
                <Typography variant="caption" sx={{ fontWeight: 600, color: net > 0 ? 'success.main' : 'error.main' }}>
                  {net > 0 ? '+' : '−'}{formatCurrency(Math.abs(net))}
                </Typography>
              )}
            </Box>
            <Stack spacing={0.75}>
              {group.items.map((transaction) => {
                const isTransfer = transaction.type === 'transfer';
                const category = transaction.category
                  ? { ...transaction.category, icon: categories.find(c => c.id === transaction.category.id)?.icon }
                  : null;
                const amountColor = transaction.type === 'income'
                  ? 'success.main'
                  : transaction.type === 'expense' ? 'error.main' : 'info.main';
                const sign = transaction.type === 'income' ? '+' : transaction.type === 'expense' ? '−' : '';
                const title = transaction.description
                  || (isTransfer ? 'Transfer' : category?.name || 'Uncategorized');
                const subtitle = isTransfer
                  ? `${transaction.wallet?.name || 'Deleted wallet'} → ${transaction.toWallet?.name || 'Deleted wallet'}`
                  : [category?.name || 'Uncategorized', transaction.wallet?.name].filter(Boolean).join(' · ');

                return (
                  <Paper
                    key={transaction.id}
                    variant="outlined"
                    onClick={() => onEdit(transaction)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      px: 1.25,
                      py: 1,
                      borderRadius: 2.5,
                      cursor: 'pointer',
                      bgcolor: 'background.paper',
                      borderColor: theme => alpha(theme.palette.divider, 0.15),
                    }}
                  >
                    {isTransfer ? (
                      <Box sx={{
                        width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: theme => alpha(theme.palette.info.main, 0.15), color: 'info.main',
                      }}>
                        <CompareArrowsIcon sx={{ fontSize: 20 }} />
                      </Box>
                    ) : (
                      <CategoryIconBadge category={category} size={34} />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                        {title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {subtitle}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: amountColor, whiteSpace: 'nowrap' }}>
                      {sign}{formatCurrency(Math.abs(transaction.amount))}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Delete transaction"
                      onClick={(e) => { e.stopPropagation(); onDelete(transaction); }}
                      sx={{ color: 'text.secondary', ml: -0.5, '&:hover': { color: 'error.main' } }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

// Distinct colours for category charts (enough that categories rarely share one)
const DISTRIBUTION_COLORS = [
  '#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#8b5cf6', '#0891b2',
  '#ec4899', '#65a30d', '#ea580c', '#0d9488', '#7c3aed', '#ca8a04',
  '#db2777', '#4f46e5', '#059669', '#64748b',
];
const MAX_SLICES = 8; // smaller categories are grouped into "Others" in the chart

// Donut chart of amounts by category, with the total in the middle and a legend
// below listing every category's amount and share.
const CategoryDistribution = ({ title, titleColor, items, emptyText }) => {
  const total = items.reduce((sum, item) => sum + Math.abs(item.amount), 0);
  const rows = items.map((item, index) => ({
    label: item.category,
    value: Math.abs(item.amount),
    color: DISTRIBUTION_COLORS[index % DISTRIBUTION_COLORS.length],
  }));
  const slices = rows.length > MAX_SLICES
    ? [
      ...rows.slice(0, MAX_SLICES - 1),
      { label: 'Others', value: rows.slice(MAX_SLICES - 1).reduce((sum, r) => sum + r.value, 0), color: '#94a3b8' },
    ]
    : rows;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, color: titleColor }}>
        {title}
      </Typography>
      {rows.length === 0 ? (
        <Box sx={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary">{emptyText}</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ position: 'relative', height: 200 }}>
            <MuiPieChart
              series={[{
                data: slices.map((slice, index) => ({ id: index, value: slice.value, label: slice.label, color: slice.color })),
                innerRadius: 62,
                outerRadius: 94,
                paddingAngle: 1.5,
                cornerRadius: 4,
                highlightScope: { faded: 'global', highlighted: 'item' },
                faded: { additionalRadius: -4, color: 'gray' },
                valueFormatter: (item) => `${formatCurrency(item.value)} (${total ? ((item.value / total) * 100).toFixed(1) : 0}%)`,
              }]}
              height={200}
              margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
              slotProps={{ legend: { hidden: true } }}
            />
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <Typography variant="caption" color="text.secondary">Total</Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {formatCurrency(total)}
              </Typography>
            </Box>
          </Box>
          <Box
            component="ul"
            sx={{ listStyle: 'none', p: 0, m: 0, mt: 1.5, maxHeight: 232, overflowY: 'auto', pr: 0.5 }}
          >
            {rows.map((row) => (
              <Box
                component="li"
                key={row.label}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}
              >
                <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: row.color, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {formatCurrency(row.value)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ width: 44, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {total ? `${((row.value / total) * 100).toFixed(1)}%` : '0%'}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

// Transaction Dialog Component
const TransactionDialog = ({ open, onClose, wallets, categories, onSave, transaction = null }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const defaultFormState = {
    type: 'expense',
    amount: '',
    description: '',
    category_id: '',
    wallet_id: '',
    toWalletId: '', // For transfers
    date: new Date().toISOString().split('T')[0],
    notes: ''
  };

  const [formData, setFormData] = useState(defaultFormState);

  useEffect(() => {
    if (transaction && open) {
      setFormData({
        type: transaction.type,
        amount: Math.abs(transaction.amount).toString(),
        description: transaction.description || '',
        category_id: transaction.category_id || '',
        wallet_id: transaction.wallet_id || '',
        toWalletId: transaction.toWallet?.id || '', // For transfers
        date: transaction.date || defaultFormState.date,
        notes: transaction.notes || ''
      });
      setFormError(null);
    } else if (!open) {
      setFormData(defaultFormState);
      setFormError(null);
      setIsSubmitting(false); // Reset loading state when dialog closes
    }
  }, [transaction, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    // Validation based on transaction type
    if (formData.type === 'transfer') {
      if (!formData.wallet_id || !formData.toWalletId) {
        setFormError('Please select both source and destination wallets');
        return;
      }
      if (formData.wallet_id === formData.toWalletId) {
        setFormError('Cannot transfer to the same wallet');
        return;
      }
    } else {
      if (!formData.wallet_id || !formData.category_id) {
        setFormError('Please select both wallet and category');
        return;
      }
    }

    const amountValue = evaluateAmount(formData.amount);
    if (amountValue === null || amountValue <= 0) {
      setFormError(isAmountExpression(formData.amount)
        ? 'Please check the calculation in Amount'
        : 'Please enter a valid amount');
      return;
    }

    // A calculation like 250+150+100 is saved as its result, and the working goes into Notes
    let notes = formData.notes || '';
    if (isAmountExpression(formData.amount)) {
      const working = `${formatAmountExpression(formData.amount)} = ${amountValue}`;
      if (!notes.includes(working)) notes = notes.trim() ? `${notes.trim()}\n${working}` : working;
    }

    try {
      setIsSubmitting(true);
      setFormError(null);
      await onSave({
        ...formData,
        amount: amountValue,
        notes
      });
    } catch (err) {
      console.error('Error saving transaction:', err);
      setFormError('Failed to save transaction: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false); // Reset loading state regardless of success or failure
    }
  };

  const amountPreview = evaluateAmount(formData.amount);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      onBackdropClick={() => {
        // Clear focus before closing
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        onClose();
      }}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          background: theme => alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(10px)',
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {transaction ? 'Edit Transaction' : 'New Transaction'}
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {formError}
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Tabs
                value={formData.type}
                onChange={(e, newValue) => {
                  setFormData(prev => ({
                    ...prev,
                    type: newValue,
                    // Clear category when switching to transfer
                    category_id: newValue === 'transfer' ? '' : prev.category_id,
                    // Clear toWalletId when switching away from transfer
                    toWalletId: newValue !== 'transfer' ? '' : prev.toWalletId
                  }));
                }}
                sx={{ mb: 2 }}
              >
                <Tab value="income" label="Income" />
                <Tab value="expense" label="Expense" />
                <Tab value="transfer" label="Transfer" />
              </Tabs>
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="amount"
                label="Amount"
                fullWidth
                required
                value={formData.amount}
                onChange={(e) => {
                  // Only digits, decimal point, brackets and + - × ÷ * / x
                  const value = e.target.value.replace(/[^0-9.,+\-*/×÷xX()\s]/g, '');
                  setFormData(prev => ({ ...prev, amount: value }));
                }}
                placeholder="e.g. 500 or 250+150+100"
                inputProps={{ inputMode: 'text', autoComplete: 'off', 'aria-describedby': 'amount-calc-hint' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
                sx={{
                  mb: 0.5,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: theme => alpha(theme.palette.grey[100], 0.8),
                    borderRadius: 3,
                    border: 'none',
                    '& fieldset': {
                      border: `1px solid ${alpha('#000', 0.1)}`,
                    },
                    '&:hover fieldset': {
                      border: `1px solid ${alpha('#000', 0.2)}`,
                    },
                    '&.Mui-focused fieldset': {
                      border: `2px solid`,
                      borderColor: 'primary.main',
                    }
                  },
                  '& input[type=number]': {
                    MozAppearance: 'textfield'
                  },
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0
                  }
                }}
              />
              {isAmountExpression(formData.amount) && (
                <Typography
                  id="amount-calc-hint"
                  variant="body2"
                  sx={{ display: 'block', fontWeight: 600, color: amountPreview > 0 ? 'primary.main' : 'error.main' }}
                >
                  {amountPreview > 0
                    ? `= ${formatCurrency(amountPreview)} · the calculation is saved in Notes`
                    : 'Keep typing… the calculation is not complete yet'}
                </Typography>
              )}
              {amountPreview > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: 'block',
                    mb: 2,
                    fontStyle: 'italic'
                  }}
                >
                  {numberToWords(Math.floor(amountPreview))} Rupees
                  {amountPreview % 1 > 0 && ` and ${Math.round((amountPreview % 1) * 100)} Paise`}
                </Typography>
              )}
            </Grid>

            {formData.type !== 'transfer' && (
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category_id"
                    value={formData.category_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                    label="Category"
                    renderValue={(value) => {
                      const selected = categories.find(cat => cat.id === value);
                      if (!selected) return '';
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <CategoryIconBadge category={selected} size={24} />
                          {selected.name}
                        </Box>
                      );
                    }}
                    sx={{
                      bgcolor: theme => alpha(theme.palette.grey[100], 0.8),
                      borderRadius: 3,
                      border: 'none',
                      '& fieldset': {
                        border: `1px solid ${alpha('#000', 0.1)}`,
                      },
                      '&:hover fieldset': {
                        border: `1px solid ${alpha('#000', 0.2)}`,
                      },
                      '&.Mui-focused fieldset': {
                        border: `2px solid`,
                        borderColor: 'primary.main',
                      }
                    }}
                  >
                    {categories
                      .filter(cat => cat.type === formData.type)
                      .map(category => (
                        <MenuItem key={category.id} value={category.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <CategoryIconBadge category={category} size={26} />
                            {category.name}
                          </Box>
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>{formData.type === 'transfer' ? 'From Wallet' : 'Wallet'}</InputLabel>
                <Select
                  name="wallet_id"
                  value={formData.wallet_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, wallet_id: e.target.value }))}
                  label={formData.type === 'transfer' ? 'From Wallet' : 'Wallet'}
                  sx={{
                    bgcolor: theme => alpha(theme.palette.grey[100], 0.8),
                    borderRadius: 3,
                    border: 'none',
                    '& fieldset': {
                      border: `1px solid ${alpha('#000', 0.1)}`,
                    },
                    '&:hover fieldset': {
                      border: `1px solid ${alpha('#000', 0.2)}`,
                    },
                    '&.Mui-focused fieldset': {
                      border: `2px solid`,
                      borderColor: 'primary.main',
                    }
                  }}
                >
                  {walletMenuItems(wallets, {
                    excludeId: formData.type === 'transfer' ? formData.toWalletId : undefined,
                    keepId: formData.wallet_id,
                  })}
                </Select>
              </FormControl>
            </Grid>

            {formData.type === 'transfer' && (
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>To Wallet</InputLabel>
                  <Select
                    name="toWalletId"
                    value={formData.toWalletId}
                    onChange={(e) => setFormData(prev => ({ ...prev, toWalletId: e.target.value }))}
                    label="To Wallet"
                    sx={{
                      bgcolor: theme => alpha(theme.palette.grey[100], 0.8),
                      borderRadius: 3,
                      border: 'none',
                      '& fieldset': {
                        border: `1px solid ${alpha('#000', 0.1)}`,
                      },
                      '&:hover fieldset': {
                        border: `1px solid ${alpha('#000', 0.2)}`,
                      },
                      '&.Mui-focused fieldset': {
                        border: `2px solid`,
                        borderColor: 'primary.main',
                      }
                    }}
                  >
                    {walletMenuItems(wallets, {
                      excludeId: formData.wallet_id,
                      keepId: formData.toWalletId,
                    })}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                fullWidth
                value={formData.description}
                helperText={formData.type !== 'transfer' && !formData.description?.trim() ? 'Leave blank to use the category name' : undefined}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: theme => alpha(theme.palette.grey[100], 0.8),
                    borderRadius: 3,
                    border: 'none',
                    '& fieldset': {
                      border: `1px solid ${alpha('#000', 0.1)}`,
                    },
                    '&:hover fieldset': {
                      border: `1px solid ${alpha('#000', 0.2)}`,
                    },
                    '&.Mui-focused fieldset': {
                      border: `2px solid`,
                      borderColor: 'primary.main',
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date"
                  value={formData.date ? dayjs(formData.date) : null}
                  onChange={(newValue) => {
                    const dateString = newValue ? newValue.format('YYYY-MM-DD') : '';
                    setFormData(prev => ({ ...prev, date: dateString }));
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      name: "date",
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          bgcolor: theme => alpha(theme.palette.grey[100], 0.8),
                          borderRadius: 3,
                          border: 'none',
                          '& fieldset': {
                            border: `1px solid ${alpha('#000', 0.1)}`,
                          },
                          '&:hover fieldset': {
                            border: `1px solid ${alpha('#000', 0.2)}`,
                          },
                          '&.Mui-focused fieldset': {
                            border: `2px solid`,
                            borderColor: 'primary.main',
                          }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes"
                fullWidth
                multiline
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: theme => alpha(theme.palette.grey[100], 0.8),
                    borderRadius: 3,
                    border: 'none',
                    '& fieldset': {
                      border: `1px solid ${alpha('#000', 0.1)}`,
                    },
                    '&:hover fieldset': {
                      border: `1px solid ${alpha('#000', 0.2)}`,
                    },
                    '&.Mui-focused fieldset': {
                      border: `2px solid`,
                      borderColor: 'primary.main',
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              isSubmitting ||
              !formData.wallet_id ||
              (formData.type === 'transfer' ? !formData.toWalletId : !formData.category_id) ||
              (formData.type === 'transfer' && formData.wallet_id === formData.toWalletId)
            }
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? 'Saving...' : (transaction ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// Main Transactions Component
const Transactions = () => {
  const { supabase, checkConnection } = useSupabase();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortField, setSortField] = useState('created_at');  // Changed from 'date' to 'created_at'
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedWallets, setSelectedWallets] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [dateRange, setDateRange] = useState(() => periodRange(DEFAULT_FILTERS.period));
  // Filters panel edits a draft; "Update" applies it (and loads the period's data)
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [appliedPeriod, setAppliedPeriod] = useState({ period: DEFAULT_FILTERS.period, custom: DEFAULT_FILTERS.custom });
  const [filterError, setFilterError] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const loadedRangeRef = useRef(periodRange(DEFAULT_FILTERS.period));
  const fetchSeqRef = useRef(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showDashboard, setShowDashboard] = useState(true);
  const [connectionError, setConnectionError] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [categoryManagementOpen, setCategoryManagementOpen] = useState(false);
  const transactionsTour = useTour('transactions');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    return transactions.filter(transaction => {
      // Text search
      const matchesSearch = searchQuery
        ? transaction.description.toLowerCase().includes(searchQuery.toLowerCase())
        : true;

      // Type filter
      const matchesType = selectedTypes.length === 0
        ? true
        : selectedTypes.includes(transaction.type);

      // Wallet filter
      const matchesWallet = selectedWallets.length === 0
        ? true
        : transaction.type === 'transfer'
          ? selectedWallets.includes(transaction.wallet?.id) || selectedWallets.includes(transaction.toWallet?.id)
          : selectedWallets.includes(transaction.wallet?.id);

      // Category filter
      const matchesCategory = selectedCategories.length === 0
        ? true
        : transaction.type === 'transfer'
          ? false // transfers don't have categories
          : selectedCategories.includes(transaction.category?.id);

      // Date range filter
      let matchesDateRange = true;
      const transactionDate = dayjs(transaction.date);

      if (dateRange[0]) {
        const startDate = dayjs(dateRange[0]);
        matchesDateRange = matchesDateRange && (
          transactionDate.isAfter(startDate) ||
          transactionDate.isSame(startDate, 'day')
        );
      }

      if (dateRange[1]) {
        const endDate = dayjs(dateRange[1]);
        matchesDateRange = matchesDateRange && (
          transactionDate.isBefore(endDate) ||
          transactionDate.isSame(endDate, 'day')
        );
      }

      return matchesSearch && matchesType && matchesWallet &&
        matchesCategory && matchesDateRange;
    });
  }, [transactions, searchQuery, selectedTypes, selectedWallets, selectedCategories, dateRange]);

  // Calculate totals and category data
  const { totalIncome, totalExpenses, netFlow, categoryData } = useMemo(() => {
    // Calculate income: regular income only
    const income = filteredTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);

    // Calculate expenses: regular expenses only
    const expenses = filteredTransactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);

    // For transfers, only consider them when wallet filter is applied
    let incomingTransfers = 0;
    let outgoingTransfers = 0;

    if (selectedWallets.length > 0) {
      // Wallet filter applied - check if transfer is IN or OUT relative to selected wallets
      filteredTransactions
        .filter(t => t.type === 'transfer')
        .forEach(t => {
          const isFromSelectedWallet = selectedWallets.includes(t.wallet?.id);
          const isToSelectedWallet = selectedWallets.includes(t.toWallet?.id);

          if (isFromSelectedWallet && !isToSelectedWallet) {
            // Transfer OUT from selected wallet
            outgoingTransfers += Math.abs(t.amount);
          } else if (!isFromSelectedWallet && isToSelectedWallet) {
            // Transfer IN to selected wallet
            incomingTransfers += Math.abs(t.amount);
          } else if (isFromSelectedWallet && isToSelectedWallet) {
            // Transfer between selected wallets - neutral (don't count as income or expense)
            // This prevents double counting when both wallets are selected
          }
        });
    }
    // If no wallet filter is applied, transfers are ignored in totals

    const totalIncome = income + incomingTransfers;
    const totalExpenses = expenses + outgoingTransfers;
    const net = totalIncome - totalExpenses;

    // Calculate category distribution for both income and expenses
    const calculateCategoryTotals = (type) => {
      return filteredTransactions
        .filter(t => t.type === type)
        .reduce((acc, transaction) => {
          const categoryName = transaction.category?.name || 'Uncategorized';
          if (!acc[categoryName]) {
            acc[categoryName] = 0;
          }
          acc[categoryName] += Math.abs(transaction.amount);
          return acc;
        }, {});
    };

    const incomeCategoryTotals = calculateCategoryTotals('income');
    const expenseCategoryTotals = calculateCategoryTotals('expense');

    const formatCategoryData = (categoryTotals) => {
      return Object.entries(categoryTotals)
        .map(([category, amount]) => ({
          category,
          amount
        }))
        .sort((a, b) => b.amount - a.amount);
    };

    return {
      totalIncome,
      totalExpenses,
      netFlow: net,
      categoryData: {
        income: formatCategoryData(incomeCategoryTotals),
        expense: formatCategoryData(expenseCategoryTotals)
      }
    };
  }, [filteredTransactions]);

  // Sort transactions
  const sortedTransactions = useMemo(() => {
    return [...filteredTransactions].sort((a, b) => {
      if (sortField === 'created_at') {
        // Sort by date first
        const dateCompare = new Date(b.date) - new Date(a.date);
        if (dateCompare !== 0) return sortOrder === 'desc' ? dateCompare : -dateCompare;

        // If dates are equal, sort by created_at
        const timeA = new Date(a.created_at).getTime();
        const timeB = new Date(b.created_at).getTime();
        return sortOrder === 'desc' ? timeB - timeA : timeA - timeB;
      } else if (sortField === 'amount') {
        const amountA = Math.abs(a.amount);
        const amountB = Math.abs(b.amount);
        return sortOrder === 'desc' ? amountB - amountA : amountA - amountB;
      }
      return 0;
    });
  }, [filteredTransactions, sortField, sortOrder]);

  // Get paginated transactions
  const paginatedTransactions = useMemo(() => {
    return sortedTransactions.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [sortedTransactions, page, rowsPerPage]);

  useEffect(() => {
    const init = async () => {
      try {
        const isConnected = await checkConnection();
        if (!isConnected) {
          setConnectionError('Unable to connect to database');
          return;
        }

        await Promise.all([
          fetchTransactions(),
          fetchCategories(),
          fetchWallets()
        ]);
      } catch (error) {
        console.error('Initialization error:', error);
        setConnectionError(error.message);
      }
    };

    init();
  }, []);

  // Show error if connection failed
  if (connectionError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Connection Error: {connectionError}
        </Typography>
      </Box>
    );
  }

  // Loads transactions for the applied date range (loadedRangeRef). Older responses
  // are ignored if the range changed while they were loading.
  const fetchTransactions = async () => {
    const seq = ++fetchSeqRef.current;
    const [rangeFrom, rangeTo] = loadedRangeRef.current;
    setLoadingTransactions(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Fetch fresh data with all related information. All pages: a single request
      // stops at 1,000 rows, which hid older transactions.
      const data = await fetchAllRows(() => {
        let query = supabase
        .from('transactions')
        .select(`
          *,
          wallet:wallets (
            id,
            name,
            type,
            balance,
            primary_card_id,
            credit_limit
          ),
          category:categories (
            id,
            name,
            type,
            color
          )
        `)
        .eq('user_id', user.id);
        if (rangeFrom) query = query.gte('date', rangeFrom.format('YYYY-MM-DD'));
        if (rangeTo) query = query.lte('date', rangeTo.format('YYYY-MM-DD'));
        return query
          .order('date', { ascending: false })
          .order('created_at', { ascending: false })
          .order('id', { ascending: true });
      });

      if (seq !== fetchSeqRef.current) return;

      // Process transfers using transfer_id
      const processedTransactions = [];
      const processedIds = new Set();

      for (const transaction of data) {
        // Skip if already processed
        if (processedIds.has(transaction.id)) continue;

        if (transaction.type === 'transfer' && transaction.transfer_id) {
          // Find matching transfer with same transfer_id
          const matchingTransfer = data.find(t =>
            !processedIds.has(t.id) &&
            t.id !== transaction.id &&
            t.type === 'transfer' &&
            t.transfer_id === transaction.transfer_id
          );

          if (matchingTransfer) {
            // Determine which is the 'from' transaction (negative amount)
            const [fromTx, toTx] = transaction.amount < 0
              ? [transaction, matchingTransfer]
              : [matchingTransfer, transaction];

            // Mark both transactions as processed
            processedIds.add(fromTx.id);
            processedIds.add(toTx.id);

            // Create combined transfer transaction
            const transferTransaction = {
              ...fromTx,
              type: 'transfer',
              amount: Math.abs(fromTx.amount),
              description: `Transfer: ${fromTx.wallet?.name || 'Deleted wallet'} → ${toTx.wallet?.name || 'Deleted wallet'}`,
              toWallet: toTx.wallet,
              transfer_id: fromTx.transfer_id,
              // Store the original transaction IDs as separate properties
              fromTransactionId: fromTx.id,
              toTransactionId: toTx.id
            };

            processedTransactions.push(transferTransaction);
          } else {
            // If no matching transfer found, treat as regular transaction
            processedIds.add(transaction.id);
            processedTransactions.push(transaction);
          }
        } else {
          // Regular transaction or transfer without transfer_id (legacy)
          processedIds.add(transaction.id);
          processedTransactions.push(transaction);
        }
      }

      setTransactions(processedTransactions);

    } catch (error) {
      if (seq !== fetchSeqRef.current) return;
      console.error('Error fetching transactions:', error);
      alert('Error loading transactions: ' + error.message);
      setTransactions([]);
    } finally {
      if (seq === fetchSeqRef.current) setLoadingTransactions(false);
    }
  };

  // Apply the filter panel: set the active filters and, if the period changed, load it
  const applyFilters = async (next) => {
    const range = periodRange(next.period, next.custom);
    if (next.period === 'custom' && !range[0] && !range[1]) {
      setFilterError('Pick a From date, a To date, or both.');
      return;
    }
    if (range[0] && range[1] && range[0].isAfter(range[1])) {
      setFilterError('The From date must be on or before the To date.');
      return;
    }
    setFilterError(null);
    setSearchQuery(next.search.trim());
    setSelectedTypes(next.types);
    setSelectedWallets(next.wallets);
    setSelectedCategories(next.categories);
    setDateRange(range);
    setAppliedPeriod({ period: next.period, custom: next.custom });
    setPage(0);
    if (!sameRange(loadedRangeRef.current, range)) {
      loadedRangeRef.current = range;
      await fetchTransactions();
    }
  };

  const handleUpdateFilters = () => applyFilters(draftFilters);

  const handleResetFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
    applyFilters(DEFAULT_FILTERS);
  };

  const filtersChanged = (
    draftFilters.search.trim() !== searchQuery
    || draftFilters.period !== appliedPeriod.period
    || (draftFilters.period === 'custom' && !sameRange(draftFilters.custom, appliedPeriod.custom))
    || JSON.stringify(draftFilters.types) !== JSON.stringify(selectedTypes)
    || JSON.stringify(draftFilters.wallets) !== JSON.stringify(selectedWallets)
    || JSON.stringify(draftFilters.categories) !== JSON.stringify(selectedCategories)
  );
  const filtersAreDefault = (
    appliedPeriod.period === DEFAULT_FILTERS.period && !searchQuery
    && selectedTypes.length === 0 && selectedWallets.length === 0 && selectedCategories.length === 0
  );
  const setDraft = (patch) => setDraftFilters(prev => ({ ...prev, ...patch }));

  const emptyState = loadingTransactions ? (
    <CircularProgress size={28} />
  ) : (
    <>
      <Typography color="text.secondary">
        No transactions for {describeRange(dateRange).toLowerCase() === 'all time' ? 'any date' : describeRange(dateRange)}
      </Typography>
      {appliedPeriod.period !== 'all' && (
        <Button
          size="small"
          sx={{ mt: 1, textTransform: 'none' }}
          onClick={() => {
            const next = { ...draftFilters, period: 'all' };
            setDraftFilters(next);
            applyFilters(next);
          }}
        >
          Show all time
        </Button>
      )}
    </>
  );

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchWallets = async () => {
    try {
      const { data, error } = await supabase
        .from('wallets')
        .select('*')
        .order('name');

      if (error) throw error;
      setWallets(data || []);
    } catch (error) {
      console.error('Error fetching wallets:', error);
    }
  };

  const updateLinkedWallets = async (primaryWalletId, transactionAmount, isNewTransaction = true) => {
    try {
      // Get primary wallet first
      const { data: primaryWallet, error: primaryError } = await supabase
        .from('wallets')
        .select('id, balance, available_balance, type, credit_limit')
        .eq('id', primaryWalletId)
        .single();

      if (primaryError) throw primaryError;

      // Only proceed if it's a credit card
      if (primaryWallet.type !== 'credit') return;

      // Get all related wallets (primary and linked)
      const { data: relatedWallets, error: fetchError } = await supabase
        .from('wallets')
        .select('id, balance, available_balance, credit_limit, linked_to, used')
        .or(`id.eq.${primaryWalletId},linked_to.eq.${primaryWalletId}`);

      if (fetchError) throw fetchError;

      // Find the actual primary wallet and linked cards
      const actualPrimaryWallet = relatedWallets.find(w => !w.linked_to);
      const linkedCards = relatedWallets.filter(w => w.linked_to);

      // Calculate total used amount across all cards
      const totalUsed = relatedWallets.reduce((sum, wallet) => sum + (wallet.used || 0), 0);

      // Calculate new available balance based on shared credit limit
      const sharedCreditLimit = actualPrimaryWallet.credit_limit;
      const newAvailableBalance = Math.max(sharedCreditLimit - totalUsed, 0);

      console.log('Credit Card Update:', {
        primaryWalletId,
        actualPrimaryId: actualPrimaryWallet.id,
        sharedCreditLimit,
        totalUsed,
        newAvailableBalance,
        linkedCards: linkedCards.length
      });

      // Update all cards with the same available balance
      const updatePromises = [
        // Update primary card
        supabase
          .from('wallets')
          .update({
            available_balance: newAvailableBalance,
            // Don't update used amount for primary card here
          })
          .eq('id', actualPrimaryWallet.id),

        // Update all linked cards
        ...linkedCards.map(wallet =>
          supabase
            .from('wallets')
            .update({
              available_balance: newAvailableBalance,
              // Don't update used amount for linked cards here
            })
            .eq('id', wallet.id)
        )
      ];

      const results = await Promise.all(updatePromises);

      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        console.error('Errors updating credit cards:', errors);
        throw new Error('Failed to update some credit cards');
      }

      console.log('Successfully updated credit cards:', {
        availableBalance: newAvailableBalance,
        totalUsed,
        updatedCards: results.length
      });
    } catch (error) {
      console.error('Error updating credit cards:', error);
      throw error;
    }
  };

  const handleSaveTransaction = async (formData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const amount = Math.abs(parseFloat(formData.amount));

      // Handle transfer transactions
      if (formData.type === 'transfer') {
        // If editing an existing transfer
        if (selectedTransaction && selectedTransaction.type === 'transfer') {
          // Use the existing handleTransferUpdate function
          await handleTransferUpdate({
            fromWalletId: formData.wallet_id,
            toWalletId: formData.toWalletId,
            amount: amount,
            date: formData.date,
            notes: formData.notes || '',
            description: formData.description || ''
          });

          // Close dialog and reset states
          setTransactionDialogOpen(false);
          setSelectedTransaction(null);
          return; // Exit early since handleTransferUpdate handles everything
        } else if (selectedTransaction && selectedTransaction.type !== 'transfer') {
          // Converting regular transaction to transfer - delete the old transaction first
          // Get the current wallet balance
          const { data: walletData, error: walletError } = await supabase
            .from('wallets')
            .select('balance')
            .eq('id', selectedTransaction.wallet_id)
            .single();

          if (walletError) throw walletError;

          // Delete the transaction
          const { error: deleteError } = await supabase
            .from('transactions')
            .delete()
            .eq('id', selectedTransaction.id);

          if (deleteError) throw deleteError;

          // Update wallet balance (reverse the old transaction)
          await supabase
            .from('wallets')
            .update({ balance: walletData.balance - selectedTransaction.amount })
            .eq('id', selectedTransaction.wallet_id);
        }

        // Create new transfer (either new or converted from regular transaction)
        await handleTransfer({
          fromWalletId: formData.wallet_id,
          toWalletId: formData.toWalletId,
          amount: amount,
          date: formData.date,
          notes: formData.notes || '',
          description: formData.description || ''
        });

        // Close dialog and reset states
        setTransactionDialogOpen(false);
        setSelectedTransaction(null);
        return; // Exit early since handleTransfer handles everything
      } else {
        // Handle regular income/expense transactions
        const newAmount = formData.type === 'expense' ? -amount : amount;
        // No description entered → use the category name
        const description = formData.description?.trim()
          || categories.find(c => c.id === formData.category_id)?.name
          || '';

        // If editing an existing transaction
        if (selectedTransaction) {
          // If changing from transfer to regular transaction, delete the transfer first
          if (selectedTransaction.type === 'transfer') {
            await handleDeleteTransfer(selectedTransaction);

            // After deleting transfer, create new regular transaction
            const { data: walletData, error: walletError } = await supabase
              .from('wallets')
              .select('balance')
              .eq('id', formData.wallet_id)
              .single();

            if (walletError) throw walletError;

            // Insert new transaction
            const { error: transactionError } = await supabase
              .from('transactions')
              .insert([{
                user_id: user.id,
                type: formData.type,
                amount: newAmount,
                description,
                category_id: formData.category_id,
                wallet_id: formData.wallet_id,
                date: formData.date,
                notes: formData.notes || ''
              }]);

            if (transactionError) throw transactionError;

            // Update wallet balance
            await supabase
              .from('wallets')
              .update({ balance: walletData.balance + newAmount })
              .eq('id', formData.wallet_id);
          } else {
            // Get the current wallet balance
            const { data: walletData, error: walletError } = await supabase
              .from('wallets')
              .select('balance')
              .eq('id', selectedTransaction.wallet_id)
              .single();

            if (walletError) throw walletError;

            // Calculate balance adjustments
            const oldAmount = selectedTransaction.amount;
            const oldWalletId = selectedTransaction.wallet_id;
            const newWalletId = formData.wallet_id;

            // Update the transaction
            const { error: updateError } = await supabase
              .from('transactions')
              .update({
                type: formData.type,
                amount: newAmount,
                description,
                category_id: formData.category_id,
                wallet_id: formData.wallet_id,
                date: formData.date,
                notes: formData.notes || '',
                transfer_id: null // Clear transfer_id for regular transactions
              })
              .eq('id', selectedTransaction.id);

            if (updateError) {
              console.error('Update error:', updateError);
              throw updateError;
            }

            // If wallet changed, update both old and new wallet balances
            if (oldWalletId !== newWalletId) {
              // Get new wallet balance
              const { data: newWalletData, error: newWalletError } = await supabase
                .from('wallets')
                .select('balance')
                .eq('id', newWalletId)
                .single();

              if (newWalletError) throw newWalletError;

              // Reverse from old wallet
              await supabase
                .from('wallets')
                .update({ balance: walletData.balance - oldAmount })
                .eq('id', oldWalletId);

              // Add to new wallet
              await supabase
                .from('wallets')
                .update({ balance: newWalletData.balance + newAmount })
                .eq('id', newWalletId);
            } else {
              // Same wallet, just update the difference
              const balanceAdjustment = newAmount - oldAmount;
              await supabase
                .from('wallets')
                .update({ balance: walletData.balance + balanceAdjustment })
                .eq('id', oldWalletId);
            }
          }


        } else {
          // For new regular transaction
          const { data: walletData, error: walletError } = await supabase
            .from('wallets')
            .select('balance')
            .eq('id', formData.wallet_id)
            .single();

          if (walletError) throw walletError;

          // Insert new transaction
          const { error: transactionError } = await supabase
            .from('transactions')
            .insert([{
              user_id: user.id,
              type: formData.type,
              amount: newAmount,
              description,
              category_id: formData.category_id,
              wallet_id: formData.wallet_id,
              date: formData.date,
              notes: formData.notes || ''
            }]);

          if (transactionError) throw transactionError;

          // Update wallet balance
          await supabase
            .from('wallets')
            .update({ balance: walletData.balance + newAmount })
            .eq('id', formData.wallet_id);
        }
      }

      // Refresh data
      await Promise.all([
        fetchTransactions(),
        fetchWallets()
      ]);

      // Close dialog and reset states
      setTransactionDialogOpen(false);
      setSelectedTransaction(null);

    } catch (error) {
      console.error('Error saving transaction:', error);
      throw error;
    }
  };

  // Update handleEditTransaction to handle all transaction types in the same dialog
  const handleEditTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setTransactionDialogOpen(true);
  };

  const handleAddCategory = () => {
    setSelectedCategory(null);
    setCategoryDialogOpen(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setCategoryDialogOpen(true);
    setCategoryManagementOpen(false);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Check if category belongs to user
      const { data: categoryData, error: categoryError } = await supabase
        .from('categories')
        .select('*')
        .eq('id', categoryId)
        .eq('user_id', user.id)
        .single();

      if (categoryError) {
        console.error('Error fetching category:', categoryError);
        alert('Error fetching category: ' + categoryError.message);
        return;
      }

      if (!categoryData) {
        alert('Category not found');
        return;
      }

      // Check if category is in use
      const { data: transactions, error: checkError } = await supabase
        .from('transactions')
        .select('id')
        .eq('category_id', categoryId)
        .limit(1);

      if (checkError) {
        console.error('Error checking category usage:', checkError);
        alert('Error checking category usage: ' + checkError.message);
        return;
      }

      if (transactions?.length > 0) {
        alert('Cannot delete category that is being used in transactions');
        return;
      }

      const { error: deleteError } = await supabase
        .from('categories')
        .delete()
        .eq('id', categoryId)
        .eq('user_id', user.id); // Ensure we only delete user's own categories

      if (deleteError) {
        console.error('Error deleting category:', deleteError);
        alert('Error deleting category: ' + deleteError.message);
        return;
      }

      // Refresh categories after successful deletion
      await fetchCategories();

    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error deleting category: ' + (error.message || 'Unknown error'));
    }
  };

  const handleSaveCategory = async (formData, categoryId = null) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const fields = {
        name: formData.name.trim(),
        type: formData.type,
        color: formData.color,
        icon: formData.icon || null
      };
      const save = (values) => (categoryId
        ? supabase.from('categories').update(values).eq('id', categoryId)
        : supabase.from('categories').insert([{ user_id: user.id, ...values }]));

      let { error } = await save(fields);

      // The icon column is added by database/2026-10-28_add_category_icon.sql. Until that
      // has been run, save the category without its icon rather than failing.
      const iconColumnMissing = error && /icon/i.test(`${error.message} ${error.details || ''}`)
        && ['PGRST204', '42703'].includes(error.code);
      if (iconColumnMissing) {
        const { icon, ...withoutIcon } = fields;
        ({ error } = await save(withoutIcon));
        if (!error && icon) {
          alert('Category saved, but its icon could not be stored yet. The database needs a one-time update (database/2026-10-28_add_category_icon.sql).');
        }
      }

      if (error) throw error;

      // Refresh categories
      await fetchCategories();
      setCategoryDialogOpen(false);
      setSelectedCategory(null);
      setCategoryManagementOpen(true);

    } catch (error) {
      console.error('Error saving category:', error);
      alert('Error saving category: ' + (error.message || 'Unknown error'));
    }
  };



  const handleTransfer = async (formData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const amount = Math.abs(parseFloat(formData.amount));

      // 1. First get both wallets' current data
      const { data: walletsData, error: walletsError } = await supabase
        .from('wallets')
        .select('*')
        .in('id', [formData.fromWalletId, formData.toWalletId]);

      if (walletsError) throw walletsError;

      const sourceWallet = walletsData.find(w => w.id === formData.fromWalletId);
      const destWallet = walletsData.find(w => w.id === formData.toWalletId);

      if (!sourceWallet || !destWallet) {
        throw new Error('Invalid wallets selected');
      }

      // 2. Generate a unique transfer_id for this transfer pair
      const { data: { session } } = await supabase.auth.getSession();
      const transferId = crypto.randomUUID();

      // 3. Create both transfer transactions with the same transfer_id
      const { data: transferPair, error: transferError } = await supabase
        .from('transactions')
        .insert([
          // Source wallet transaction (negative amount)
          {
            user_id: user.id,
            type: 'transfer',
            amount: -amount,
            description: formData.description || `Transfer to ${destWallet.name}`,
            wallet_id: sourceWallet.id,
            date: formData.date,
            notes: formData.notes,
            transfer_id: transferId
          },
          // Destination wallet transaction (positive amount)
          {
            user_id: user.id,
            type: 'transfer',
            amount: amount,
            description: formData.description || `Transfer from ${sourceWallet.name}`,
            wallet_id: destWallet.id,
            date: formData.date,
            notes: formData.notes,
            transfer_id: transferId
          }
        ])
        .select();

      if (transferError) throw transferError;

      if (!transferPair || transferPair.length !== 2) {
        throw new Error('Failed to create transfer transactions');
      }

      // 4. Update both wallet balances
      const { error: updateError } = await supabase
        .from('wallets')
        .upsert([
          { ...sourceWallet, balance: sourceWallet.balance - amount },
          { ...destWallet, balance: destWallet.balance + amount }
        ]);

      if (updateError) throw updateError;

      // 5. Refresh the UI data
      await Promise.all([
        fetchTransactions(),
        fetchWallets()
      ]);

    } catch (error) {
      console.error('Transfer error:', error);
      throw error;
    }
  };

  // Update handleDeleteTransfer to use transfer_id
  const handleDeleteTransfer = async (transaction) => {
    if (!transaction?.type === 'transfer') {
      throw new Error('Invalid transfer transaction');
    }

    try {
      const transferId = transaction.transfer_id;

      if (!transferId) {
        throw new Error('Invalid transfer_id');
      }

      // 1. Get both transfer transactions using transfer_id
      const { data: transferPair, error: searchError } = await supabase
        .from('transactions')
        .select('*')
        .eq('transfer_id', transferId);

      if (searchError) {
        throw new Error(`Failed to find transfer pair: ${searchError.message}`);
      }

      if (!transferPair || transferPair.length !== 2) {
        throw new Error(`Expected 2 transactions, found ${transferPair?.length || 0}`);
      }

      // Sort transactions to identify source and destination
      const [sourceTx, destTx] = transferPair.sort((a, b) => a.amount - b.amount);
      const transferAmount = Math.abs(sourceTx.amount);

      // 2. Get both wallets
      const { data: walletsData, error: walletsError } = await supabase
        .from('wallets')
        .select('*')
        .in('id', [sourceTx.wallet_id, destTx.wallet_id]);

      if (walletsError) {
        throw new Error(`Failed to fetch wallets: ${walletsError.message}`);
      }

      if (!walletsData || walletsData.length !== 2) {
        throw new Error(`Expected 2 wallets, found ${walletsData?.length || 0}`);
      }

      const sourceWallet = walletsData.find(w => w.id === sourceTx.wallet_id);
      const destWallet = walletsData.find(w => w.id === destTx.wallet_id);

      if (!sourceWallet || !destWallet) {
        throw new Error('Could not match wallets to transactions');
      }

      // 3. Delete both transactions using transfer_id
      const { error: deleteError } = await supabase
        .from('transactions')
        .delete()
        .eq('transfer_id', transferId);

      if (deleteError) {
        throw new Error(`Failed to delete transactions: ${deleteError.message}`);
      }

      // 4. Update wallet balances by reversing the transfer
      const { error: updateError } = await supabase
        .from('wallets')
        .upsert([
          {
            ...sourceWallet,
            balance: sourceWallet.balance + transferAmount // Add back to source
          },
          {
            ...destWallet,
            balance: destWallet.balance - transferAmount // Remove from destination
          }
        ]);

      if (updateError) {
        throw new Error(`Failed to update wallet balances: ${updateError.message}`);
      }

      // 5. Refresh the UI data
      await Promise.all([
        fetchTransactions(),
        fetchWallets()
      ]);

    } catch (error) {
      console.error('Error deleting transfer:', error);
      throw error;
    }
  };

  // Update handleDeleteTransaction with better error handling
  const handleDeleteTransaction = async (transaction) => {
    if (!transaction) {
      console.error('No transaction provided for deletion');
      return;
    }

    try {
      if (transaction.type === 'transfer') {
        await handleDeleteTransfer(transaction);
      } else {
        // Handle regular transaction deletion
        const { data: walletData, error: walletError } = await supabase
          .from('wallets')
          .select('*')
          .eq('id', transaction.wallet_id)
          .single();

        if (walletError) {
          throw new Error(`Failed to fetch wallet: ${walletError.message}`);
        }

        if (!walletData) {
          throw new Error('Wallet not found');
        }

        // Delete the transaction
        const { error: deleteError } = await supabase
          .from('transactions')
          .delete()
          .eq('id', transaction.id);

        if (deleteError) {
          throw new Error(`Failed to delete transaction: ${deleteError.message}`);
        }

        // Update wallet balance
        const { error: updateError } = await supabase
          .from('wallets')
          .upsert({
            ...walletData,
            balance: walletData.balance - transaction.amount
          });

        if (updateError) {
          throw new Error(`Failed to update wallet balance: ${updateError.message}`);
        }

        await Promise.all([
          fetchTransactions(),
          fetchWallets()
        ]);
      }
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert(error.message || 'Error deleting transaction');
    }
  };

  // Add pagination options
  const paginationOptions = [10, 25, 50, 75, 100];

  // Handler functions
  const handleClearFilters = () => {
    setSortField('created_at');
    setSortOrder('desc');
    handleResetFilters();
  };

  const handleSort = (field) => {
    const newOrder = field === sortField && sortOrder === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortOrder(newOrder);
  };

  // Download Functions
  const handleDownloadCSV = () => {
    if (filteredTransactions.length === 0) return;

    setDownloadingCSV(true);
    try {
      // Prepare CSV data
      const csvHeaders = [
        'Date',
        'Description',
        'Category',
        'Type',
        'Wallet',
        'Amount',
        'Notes'
      ];

      const csvData = filteredTransactions.map(transaction => [
        formatDate(transaction.date),
        `"${(transaction.description || '').replace(/"/g, '""')}"`, // Escape quotes
        transaction.type === 'transfer' ? 'Transfer' : (transaction.category?.name || 'Uncategorized'),
        transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1),
        transaction.type === 'transfer' 
          ? `${transaction.wallet?.name} → ${transaction.toWallet?.name}`
          : transaction.wallet?.name || '',
        transaction.type === 'expense' ? `-${Math.abs(transaction.amount)}` : Math.abs(transaction.amount),
        transaction.notes ? `"${transaction.notes.replace(/"/g, '""')}"` : ''
      ]);

      // Create CSV content
      const csvContent = [
        // Header with summary info
        `Transaction Report - Generated on ${new Date().toLocaleDateString()}`,
        `Total Income: ${formatCurrency(totalIncome)}`,
        `Total Expenses: ${formatCurrency(Math.abs(totalExpenses))}`,
        `Net Flow: ${formatCurrency(netFlow)}`,
        `Total Transactions: ${filteredTransactions.length}`,
        '',
        // Transaction headers
        csvHeaders.join(','),
        // Transaction data
        ...csvData.map(row => row.join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `Transactions_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error generating CSV:', error);
      alert('Error generating CSV file');
    } finally {
      setDownloadingCSV(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (filteredTransactions.length === 0) return;

    setDownloadingPDF(true);
    try {
      // Import only jsPDF, avoid autoTable completely
      const jsPDFModule = await import('jspdf');
      const jsPDF = jsPDFModule.default || jsPDFModule.jsPDF;
      
      // Create new PDF document
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      const rowHeight = 8;
      const headerHeight = 115; // Space for header and summary
      const footerHeight = 20; // Space for footer
      const availableHeight = pageHeight - headerHeight - footerHeight;
      const maxRowsPerPage = Math.floor(availableHeight / rowHeight);

      // Prepare table data for ALL transactions
      const tableData = filteredTransactions.map(transaction => {
        const description = (transaction.description || '').substring(0, 25);
        const category = transaction.type === 'transfer' 
          ? 'Transfer' 
          : (transaction.category?.name || 'Uncategorized');
        const wallet = transaction.type === 'transfer'
          ? `${transaction.wallet?.name} → ${transaction.toWallet?.name}`.substring(0, 20)
          : (transaction.wallet?.name || '').substring(0, 15);
        const amount = (transaction.type === 'expense' ? '-' : '') + formatCurrency(Math.abs(transaction.amount));

        return [
          formatDate(transaction.date),
          description,
          category.substring(0, 15),
          transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1),
          wallet,
          amount
        ];
      });

      // Function to add header to each page
      const addHeader = (pageNum, totalPages) => {
        // Header
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text('Transaction Report', margin, 30);

        // Page number
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - margin - 30, 30);

        // Summary info (only on first page)
        if (pageNum === 1) {
          doc.setFontSize(12);
          doc.setFont('helvetica', 'normal');
          doc.text(`Generated on: ${new Date().toLocaleDateString()}`, margin, 45);
          doc.text(`Total Income: ${formatCurrency(totalIncome)}`, margin, 55);
          doc.text(`Total Expenses: ${formatCurrency(Math.abs(totalExpenses))}`, margin, 65);
          doc.text(`Net Flow: ${formatCurrency(netFlow)}`, margin, 75);
          doc.text(`Total Transactions: ${filteredTransactions.length}`, margin, 85);
        }

        // Table header
        let yPosition = pageNum === 1 ? 105 : 40;
        
        // Add a line separator
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, yPosition - 5, pageWidth - margin, yPosition - 5);
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(25, 118, 210);
        doc.text('Date', margin, yPosition);
        doc.text('Description', margin + 25, yPosition);
        doc.text('Category', margin + 60, yPosition);
        doc.text('Type', margin + 90, yPosition);
        doc.text('Wallet', margin + 110, yPosition);
        doc.text('Amount', margin + 150, yPosition);
        yPosition += 5;
        
        // Header underline
        doc.setDrawColor(25, 118, 210);
        doc.line(margin, yPosition, pageWidth - margin, yPosition);
        
        return yPosition + 10; // Return starting position for data rows
      };

      // Calculate total pages needed
      const totalPages = Math.ceil(tableData.length / maxRowsPerPage);
      
      // Process all transactions across multiple pages
      let currentRow = 0;
      
      for (let page = 1; page <= totalPages; page++) {
        if (page > 1) {
          doc.addPage();
        }
        
        let yPosition = addHeader(page, totalPages);
        
        // Calculate rows for this page
        const startRow = (page - 1) * maxRowsPerPage;
        const endRow = Math.min(startRow + maxRowsPerPage, tableData.length);
        const rowsOnThisPage = endRow - startRow;
        
        // Add table rows for this page
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        
        for (let i = startRow; i < endRow; i++) {
          const row = tableData[i];
          const rowIndex = i - startRow;
          
          // Alternate row colors
          if (rowIndex % 2 === 0) {
            doc.setTextColor(60, 60, 60);
          } else {
            doc.setTextColor(0, 0, 0);
          }
          
          doc.text(String(row[0] || ''), margin, yPosition);
          doc.text(String(row[1] || ''), margin + 25, yPosition);
          doc.text(String(row[2] || ''), margin + 60, yPosition);
          doc.text(String(row[3] || ''), margin + 90, yPosition);
          doc.text(String(row[4] || ''), margin + 110, yPosition);
          
          // Right-align amount
          const amountText = String(row[5] || '');
          const amountWidth = doc.getTextWidth(amountText);
          doc.text(amountText, pageWidth - margin - amountWidth, yPosition);
          
          yPosition += rowHeight;
        }
        
        // Add footer with transaction count for this page
        doc.setTextColor(100, 100, 100);
        doc.setFontSize(9);
        doc.text(
          `Showing transactions ${startRow + 1}-${endRow} of ${tableData.length}`, 
          margin, 
          pageHeight - 10
        );
      }

      // Save the PDF
      const fileName = `Transactions_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try CSV download instead.');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handleDownloadHTML = () => {
    if (filteredTransactions.length === 0) return;

    try {
      // Create HTML content
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Transaction Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .summary { background: #f5f5f5; padding: 15px; margin: 20px 0; border-radius: 8px; }
            .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; }
            .summary-item { text-align: center; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #1976d2; color: white; }
            .amount { text-align: right; }
            .income { color: #2e7d32; }
            .expense { color: #d32f2f; }
            .transfer { color: #1976d2; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .category-chip { 
              display: inline-block; 
              padding: 2px 8px; 
              border-radius: 12px; 
              font-size: 12px; 
              background: #e3f2fd; 
              color: #1976d2; 
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Transaction Report</h1>
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div class="summary">
            <h3>Summary</h3>
            <div class="summary-grid">
              <div class="summary-item">
                <strong>Total Income</strong><br>
                <span class="income">${formatCurrency(totalIncome)}</span>
              </div>
              <div class="summary-item">
                <strong>Total Expenses</strong><br>
                <span class="expense">${formatCurrency(Math.abs(totalExpenses))}</span>
              </div>
              <div class="summary-item">
                <strong>Net Flow</strong><br>
                <span class="${netFlow >= 0 ? 'income' : 'expense'}">${formatCurrency(netFlow)}</span>
              </div>
              <div class="summary-item">
                <strong>Total Transactions</strong><br>
                ${filteredTransactions.length}
              </div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Category</th>
                <th>Type</th>
                <th>Wallet</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${filteredTransactions.map(transaction => `
                <tr>
                  <td>${formatDate(transaction.date)}</td>
                  <td>${transaction.description || ''}</td>
                  <td>
                    ${transaction.type === 'transfer' 
                      ? '<span class="category-chip">Transfer</span>' 
                      : `<span class="category-chip">${transaction.category?.name || 'Uncategorized'}</span>`
                    }
                  </td>
                  <td>${transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}</td>
                  <td>
                    ${transaction.type === 'transfer'
                      ? `${transaction.wallet?.name || ''} → ${transaction.toWallet?.name || ''}`
                      : transaction.wallet?.name || ''
                    }
                  </td>
                  <td class="amount ${transaction.type}">
                    ${transaction.type === 'expense' ? '-' : ''}${formatCurrency(Math.abs(transaction.amount))}
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;

      // Create blob and download
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Transactions_${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating HTML:', error);
      alert('Error generating HTML file');
    }
  };

  // Update handleTransferUpdate to use transfer_id
  const handleTransferUpdate = async (formData) => {
    if (!selectedTransaction || selectedTransaction.type !== 'transfer') return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const newAmount = Math.abs(parseFloat(formData.amount));

      // 1. Get the original transactions using transfer_id
      const transferId = selectedTransaction.transfer_id;
      if (!transferId) {
        throw new Error('Invalid transfer_id for selected transfer');
      }

      const { data: originalTransactions, error: searchError } = await supabase
        .from('transactions')
        .select('*')
        .eq('transfer_id', transferId);

      if (searchError) {
        throw new Error(`Failed to find original transactions: ${searchError.message}`);
      }

      if (!originalTransactions || originalTransactions.length !== 2) {
        throw new Error(`Expected 2 transactions, found ${originalTransactions?.length || 0}`);
      }

      // Sort transactions by amount to identify source and destination
      const [oldSourceTx, oldDestTx] = originalTransactions.sort((a, b) => a.amount - b.amount);
      const oldAmount = Math.abs(oldSourceTx.amount);

      // 2. Get current wallet data
      const { data: walletsData, error: walletsError } = await supabase
        .from('wallets')
        .select('*')
        .in('id', [formData.fromWalletId, formData.toWalletId]);

      if (walletsError) {
        throw new Error(`Failed to fetch wallets: ${walletsError.message}`);
      }

      if (!walletsData || walletsData.length !== 2) {
        throw new Error('Could not find both wallets');
      }

      const sourceWallet = walletsData.find(w => w.id === formData.fromWalletId);
      const destWallet = walletsData.find(w => w.id === formData.toWalletId);

      if (!sourceWallet || !destWallet) {
        throw new Error('One or both wallets not found');
      }

      // 3. Delete both original transactions using transfer_id
      const { error: deleteError } = await supabase
        .from('transactions')
        .delete()
        .eq('transfer_id', transferId);

      if (deleteError) {
        throw new Error(`Failed to delete transactions: ${deleteError.message}`);
      }

      // 4. Generate new transfer_id and create new transfer transactions
      const newTransferId = crypto.randomUUID();

      const { data: transferData, error: transferError } = await supabase
        .from('transactions')
        .insert([
          // Source wallet transaction (negative amount)
          {
            user_id: user.id,
            type: 'transfer',
            amount: -newAmount,
            description: formData.description || `Transfer to ${destWallet.name}`,
            wallet_id: sourceWallet.id,
            date: formData.date,
            notes: formData.notes,
            transfer_id: newTransferId
          },
          // Destination wallet transaction (positive amount)
          {
            user_id: user.id,
            type: 'transfer',
            amount: newAmount,
            description: formData.description || `Transfer from ${sourceWallet.name}`,
            wallet_id: destWallet.id,
            date: formData.date,
            notes: formData.notes,
            transfer_id: newTransferId
          }
        ])
        .select();

      if (transferError) {
        throw new Error(`Failed to create new transfer: ${transferError.message}`);
      }

      // 5. Update wallet balances
      // First reverse the old transfer amounts, then apply the new ones
      const updatedSourceBalance = sourceWallet.balance + oldAmount - newAmount;
      const updatedDestBalance = destWallet.balance - oldAmount + newAmount;

      const { error: updateError } = await supabase
        .from('wallets')
        .upsert([
          {
            ...sourceWallet,
            balance: updatedSourceBalance
          },
          {
            ...destWallet,
            balance: updatedDestBalance
          }
        ]);

      if (updateError) {
        throw new Error(`Failed to update wallet balances: ${updateError.message}`);
      }

      // 6. Refresh data
      await Promise.all([
        fetchTransactions(),
        fetchWallets()
      ]);

    } catch (error) {
      console.error('Error updating transfer:', error);
      throw error;
    }
  };

  // Update handleDeleteClick to include confirmation
  const handleDeleteClick = async (transaction) => {
    // Show confirmation dialog
    const isConfirmed = window.confirm(
      transaction.type === 'transfer'
        ? `Are you sure you want to delete this transfer of ${formatCurrency(Math.abs(transaction.amount))} from ${transaction.wallet.name} to ${transaction.toWallet.name}?`
        : `Are you sure you want to delete this ${transaction.type} of ${formatCurrency(Math.abs(transaction.amount))} ${transaction.type === 'expense' ? 'from' : 'to'} ${transaction.wallet.name}?`
    );

    if (!isConfirmed) {
      return;
    }

    try {
      if (transaction.type === 'transfer') {
        await handleDeleteTransfer(transaction);
      } else {
        // Handle regular transaction deletion
        const { data: walletData, error: walletError } = await supabase
          .from('wallets')
          .select('*')
          .eq('id', transaction.wallet_id)
          .single();

        if (walletError) {
          throw new Error(`Failed to fetch wallet: ${walletError.message}`);
        }

        if (!walletData) {
          throw new Error('Wallet not found');
        }

        // Delete the transaction
        const { error: deleteError } = await supabase
          .from('transactions')
          .delete()
          .eq('id', transaction.id);

        if (deleteError) {
          throw new Error(`Failed to delete transaction: ${deleteError.message}`);
        }

        // Update wallet balance
        const { error: updateError } = await supabase
          .from('wallets')
          .upsert({
            ...walletData,
            balance: walletData.balance - transaction.amount
          });

        if (updateError) {
          throw new Error(`Failed to update wallet balance: ${updateError.message}`);
        }

        await Promise.all([
          fetchTransactions(),
          fetchWallets()
        ]);
      }
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert(error.message || 'Error deleting transaction');
    }
  };

  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 4 },
      minHeight: '100vh'
    }}>
      {/* Header Section */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 2, sm: 3 }
      }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.5rem', sm: '2rem' },
            color: 'text.primary',
          }}
        >
          Transactions
        </Typography>

        {/* Action Buttons */}
        <Box sx={{ mt: { xs: 2, sm: 0 }, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TourButton onClick={transactionsTour.start} />
          <Button
            variant="contained"
            onClick={() => setTransactionDialogOpen(true)}
            startIcon={<AddIcon />}
            data-tour="transactions-add"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: 2
            }}
          >
            Add Transaction
          </Button>
          <Button
            variant="outlined"
            onClick={() => setCategoryManagementOpen(true)}
            startIcon={<CategoryIcon />}
            data-tour="transactions-categories"
            sx={{
              borderRadius: 2,
              textTransform: 'none'
            }}
          >
            Categories
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/analysis')}
            startIcon={<InsightsOutlinedIcon />}
            data-tour="transactions-analysis"
            sx={{
              borderRadius: 2,
              textTransform: 'none'
            }}
          >
            Analysis
          </Button>
          <Box data-tour="transactions-export" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={handleDownloadCSV}
            disabled={filteredTransactions.length === 0 || downloadingCSV}
            startIcon={downloadingCSV ? <CircularProgress size={20} /> : <FileDownloadOutlinedIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none'
            }}
          >
            {downloadingCSV ? 'Generating...' : 'Download CSV'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleDownloadPDF}
            disabled={filteredTransactions.length === 0 || downloadingPDF}
            startIcon={downloadingPDF ? <CircularProgress size={20} /> : <FileDownloadOutlinedIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none'
            }}
          >
            {downloadingPDF ? 'Generating...' : 'Download PDF'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleDownloadHTML}
            disabled={filteredTransactions.length === 0}
            startIcon={<FileDownloadOutlinedIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none'
            }}
          >
            Download HTML
          </Button>
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }} data-tour="transactions-summary">
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: '16px',
            background: theme => alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(10px)',
            boxShadow: 'var(--fx-card-shadow)'
          }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Income
              </Typography>
              <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: 'success.main' }}>
                {formatCurrency(totalIncome)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: '16px',
            background: theme => alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(10px)',
            boxShadow: 'var(--fx-card-shadow)'
          }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Expenses
              </Typography>
              <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: 'error.main' }}>
                {formatCurrency(Math.abs(totalExpenses))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: '16px',
            background: theme => alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(10px)',
            boxShadow: 'var(--fx-card-shadow)'
          }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Net Flow
              </Typography>
              <Typography
                variant="h5"
                component="div"
                sx={{
                  fontWeight: 600,
                  color: netFlow >= 0 ? 'success.main' : 'error.main'
                }}
              >
                {formatCurrency(netFlow)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: '16px',
            background: theme => alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(10px)',
            boxShadow: 'var(--fx-card-shadow)'
          }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Transactions
              </Typography>
              <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                {filteredTransactions.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Filters and Search Section */}
        <Grid item xs={12}>
          <Paper data-tour="transactions-filters" sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: '16px',
            background: theme => alpha(theme.palette.background.paper, 0.6),
            backdropFilter: 'blur(10px)'
          }}>
            {/* Period */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <EventOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, mr: 0.5 }}>Period</Typography>
              {PERIODS.map(option => (
                <Chip
                  key={option.value}
                  label={option.label}
                  clickable
                  onClick={() => setDraft({ period: option.value })}
                  color={draftFilters.period === option.value ? 'primary' : 'default'}
                  variant={draftFilters.period === option.value ? 'filled' : 'outlined'}
                  aria-pressed={draftFilters.period === option.value}
                  sx={{ fontWeight: 500 }}
                />
              ))}
            </Box>

            {draftFilters.period === 'custom' && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2, maxWidth: 520 }}>
                  <DatePicker
                    label="From"
                    value={draftFilters.custom[0]}
                    onChange={(value) => setDraft({ custom: [value, draftFilters.custom[1]] })}
                    format="DD/MM/YYYY"
                    slotProps={{ textField: { fullWidth: true, size: 'small', sx: { bgcolor: 'background.paper', borderRadius: 1 } } }}
                  />
                  <DatePicker
                    label="To"
                    value={draftFilters.custom[1]}
                    onChange={(value) => setDraft({ custom: [draftFilters.custom[0], value] })}
                    format="DD/MM/YYYY"
                    slotProps={{ textField: { fullWidth: true, size: 'small', sx: { bgcolor: 'background.paper', borderRadius: 1 } } }}
                  />
                </Stack>
              </LocalizationProvider>
            )}

            <Grid container spacing={1.5} alignItems="center">
              {/* Search */}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search description..."
                  value={draftFilters.search}
                  onChange={(e) => setDraft({ search: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateFilters(); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: draftFilters.search && (
                      <InputAdornment position="end">
                        <IconButton size="small" aria-label="Clear search" onClick={() => setDraft({ search: '' })}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', borderRadius: 2 } }}
                />
              </Grid>

              {/* Transaction Type */}
              <Grid item xs={12} sm={4} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    multiple
                    value={draftFilters.types}
                    onChange={(e) => setDraft({ types: e.target.value })}
                    input={<OutlinedInput label="Type" />}
                    renderValue={(selected) => selected.map(v => v.charAt(0).toUpperCase() + v.slice(1)).join(', ')}
                    sx={{ bgcolor: 'background.paper' }}
                  >
                    {transactionTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Checkbox size="small" checked={draftFilters.types.includes(type.value)} />
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>{type.icon}</Box>
                        <ListItemText primary={type.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Wallet */}
              <Grid item xs={12} sm={4} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Wallet</InputLabel>
                  <Select
                    multiple
                    value={draftFilters.wallets}
                    onChange={(e) => setDraft({ wallets: e.target.value })}
                    input={<OutlinedInput label="Wallet" />}
                    renderValue={(selected) => selected.map(id => wallets.find(w => w.id === id)?.name || id).join(', ')}
                    sx={{ bgcolor: 'background.paper' }}
                  >
                    {wallets.map((wallet) => (
                      <MenuItem key={wallet.id} value={wallet.id}>
                        <Checkbox size="small" checked={draftFilters.wallets.includes(wallet.id)} />
                        <ListItemText primary={wallet.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Category */}
              <Grid item xs={12} sm={4} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    multiple
                    value={draftFilters.categories}
                    onChange={(e) => setDraft({ categories: e.target.value })}
                    input={<OutlinedInput label="Category" />}
                    renderValue={(selected) => selected.map(id => categories.find(c => c.id === id)?.name || id).join(', ')}
                    sx={{ bgcolor: 'background.paper' }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                  >
                    {['expense', 'income'].map(type => [
                      <ListSubheader key={`h-${type}`}>{type === 'expense' ? 'Expense' : 'Income'}</ListSubheader>,
                      ...categories.filter(c => c.type === type).map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          <Checkbox size="small" checked={draftFilters.categories.includes(category.id)} />
                          <CategoryIconBadge category={category} size={22} sx={{ mr: 1 }} />
                          <ListItemText primary={category.name} />
                        </MenuItem>
                      ))
                    ])}
                  </Select>
                </FormControl>
              </Grid>

              {/* Actions */}
              <Grid item xs={12} md={3}>
                <Stack direction="row" spacing={1} justifyContent={{ xs: 'stretch', md: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleUpdateFilters}
                    disabled={loadingTransactions && !filtersChanged}
                    startIcon={loadingTransactions ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                    sx={{ flex: { xs: 1, md: 'none' }, borderRadius: 2, textTransform: 'none', px: 2.5 }}
                  >
                    {filtersChanged ? 'Update' : 'Refresh'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    disabled={filtersAreDefault && !filtersChanged}
                    startIcon={<FilterAltOffIcon />}
                    sx={{ flex: { xs: 1, md: 'none' }, borderRadius: 2, textTransform: 'none' }}
                  >
                    Reset
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            {filterError && (
              <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>{filterError}</Typography>
            )}

            {/* What's showing + sort (sort applies straight away) */}
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  Showing <b>{describeRange(dateRange)}</b>
                  {' · '}{filteredTransactions.length} transaction{filteredTransactions.length === 1 ? '' : 's'}
                </Typography>
                {filtersChanged && (
                  <Chip size="small" color="warning" variant="outlined" label="Press Update to apply your changes" />
                )}
              </Box>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortOptions.find(opt => opt.field === sortField && opt.order === sortOrder)?.value || 'latest'}
                  onChange={(e) => {
                    const option = sortOptions.find(opt => opt.value === e.target.value);
                    if (option) {
                      setSortField(option.field);
                      setSortOrder(option.order);
                    }
                  }}
                  label="Sort By"
                  sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                >
                  {sortOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Paper>
        </Grid>

        {/* Transactions Table */}
        <Grid item xs={12} md={8}>
          <Paper data-tour="transactions-history" sx={{
            p: { xs: 1, sm: 2 },
            borderRadius: '16px',
            background: theme => alpha(theme.palette.background.paper, 0.4),
            backdropFilter: 'blur(10px)',
            height: '100%'
          }}>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Transaction History
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {describeRange(dateRange)}
              </Typography>
            </Box>
            {isMobile ? (
              <Box sx={{ opacity: loadingTransactions ? 0.5 : 1, transition: 'opacity .15s' }}>
                {paginatedTransactions.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>{emptyState}</Box>
                ) : (
                  <MobileTransactionList
                    transactions={paginatedTransactions}
                    categories={categories}
                    onEdit={handleEditTransaction}
                    onDelete={handleDeleteClick}
                  />
                )}
              </Box>
            ) : (
            <TableContainer sx={{ position: 'relative', opacity: loadingTransactions ? 0.5 : 1, transition: 'opacity .15s' }}>
              <Table sx={{
                '& .MuiTableCell-root': {
                  borderBottom: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`
                }
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Wallet</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTransactions.map((transaction) => (
                    <TableRow
                      key={transaction.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: theme => alpha(theme.palette.action.hover, 0.1)
                        }
                      }}
                    >
                      <TableCell>
                        {formatDate(transaction.date)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {transaction.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {transaction.type === 'transfer' ? (
                          <Chip
                            label="Transfer"
                            size="small"
                            icon={<CompareArrowsIcon />}
                            sx={{
                              bgcolor: theme => alpha(theme.palette.info.main, 0.1),
                              color: 'info.main'
                            }}
                          />
                        ) : (
                          <Chip
                            label={transaction.category?.name || 'Uncategorized'}
                            size="small"
                            sx={{
                              bgcolor: transaction.category?.color
                                ? alpha(transaction.category.color, 0.1)
                                : alpha('#666', 0.1),
                              color: transaction.category?.color || '#666'
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {transaction.type === 'transfer' ? (
                          <Box>
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {transaction.wallet?.name}
                              <ArrowDownward sx={{ fontSize: 16, color: 'text.secondary' }} />
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {transaction.toWallet?.name}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2">
                            {transaction.wallet?.name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{
                            color: transaction.type === 'income'
                              ? 'success.main'
                              : transaction.type === 'expense'
                                ? 'error.main'
                                : 'info.main',
                            fontWeight: 500
                          }}
                        >
                          {formatCurrency(Math.abs(transaction.amount))}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEditTransaction(transaction)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'primary.main' }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(transaction)}
                          sx={{
                            color: 'text.secondary',
                            '&:hover': { color: 'error.main' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {paginatedTransactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        {emptyState}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            )}
            <TablePagination
              component="div"
              count={sortedTransactions.length}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage={isMobile ? 'Rows' : 'Rows per page:'}
              sx={{
                borderTop: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                '.MuiTablePagination-select': {
                  background: theme => alpha(theme.palette.background.paper, 0.5),
                  backdropFilter: 'blur(10px)',
                  borderRadius: 1,
                  border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }
              }}
            />
          </Paper>
        </Grid>

        {/* Charts Section */}
        <Grid item xs={12} md={4}>
          <Paper data-tour="transactions-charts" sx={{
            p: 2,
            borderRadius: '16px',
            background: theme => alpha(theme.palette.background.paper, 0.4),
            backdropFilter: 'blur(10px)',
            height: '100%'
          }}>
            <Stack spacing={4}>
              <CategoryDistribution
                title="Income Distribution"
                titleColor="success.main"
                items={categoryData.income}
                emptyText="No income data available"
              />
              <CategoryDistribution
                title="Expense Distribution"
                titleColor="error.main"
                items={categoryData.expense}
                emptyText="No expense data available"
              />
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Dialogs */}

      {/* Dialogs */}
      <TransactionDialog
        open={transactionDialogOpen}
        onClose={() => {
          setTransactionDialogOpen(false);
          setSelectedTransaction(null);
        }}
        onSave={async (formData) => {
          try {
            await handleSaveTransaction(formData);
          } catch (error) {
            console.error('Error saving transaction:', error);
            alert(error.message || 'Error saving transaction');
          }
        }}
        transaction={selectedTransaction}
        wallets={wallets}
        categories={categories}
      />



      <CategoryDialog
        open={categoryDialogOpen}
        onClose={() => {
          setCategoryDialogOpen(false);
          setSelectedCategory(null);
        }}
        onSave={handleSaveCategory}
        category={selectedCategory}
      />

      <CategoryManagementDialog
        open={categoryManagementOpen}
        onClose={() => setCategoryManagementOpen(false)}
        categories={categories}
        onEdit={handleEditCategory}
        onDelete={handleDeleteCategory}
      />
      <GuidedTour steps={TOURS.transactions} open={transactionsTour.open} onClose={transactionsTour.close} />
    </Box>
  );
};

export default Transactions;